/**
 * Pure Threads API request-shape helpers. Same extraction pattern as
 * `shared/tiktok-post-params.ts` / `shared/facebook-post-params.ts` — lives in
 * `shared/` (not `supabase/functions/_shared/`) because this repo's
 * `test:unit:ci` (`vitest.config.ts`) EXCLUDES `supabase/**` entirely, so any
 * pure logic that needs a real Vitest gate has to live outside that tree and
 * get re-exported through a `_shared/` stub for the edge function to import.
 *
 * Verified against Meta's own Threads docs (2026-07-25):
 *   - https://developers.facebook.com/documentation/threads/posts
 *   - https://developers.facebook.com/docs/threads/get-started
 *   - https://developers.facebook.com/docs/threads/get-started/long-lived-tokens
 *
 * ⚠️ Threads is NOT on the Facebook Graph host. Base URL is
 * `https://graph.threads.net/v1.0` — no other Mora client points there, and an
 * Instagram/Facebook client cannot be reused for it.
 *
 * Publish flow (2-step for single, 3-step for carousel):
 *   1. container:  POST /{threads-user-id}/threads
 *                  media_type = TEXT | IMAGE | VIDEO  (+ is_carousel_item for children)
 *   2. carousel:   POST /{threads-user-id}/threads    media_type=CAROUSEL, children=[ids]
 *   3. publish:    POST /{threads-user-id}/threads_publish   creation_id=<id>
 *
 * Meta documents "wait on average 30 seconds before publishing" — the caller
 * polls container status rather than sleeping a fixed interval (an average
 * implies variance in both directions).
 */

/** post_info text ceiling for a Threads post, in characters. */
export const THREADS_TEXT_MAX_CHARS = 500;
/** Carousel children bounds — "limited to 20 images, videos, or a mix", "minimum of two children". */
export const THREADS_CAROUSEL_MIN_CHILDREN = 2;
export const THREADS_CAROUSEL_MAX_CHILDREN = 20;
/** Video ceiling: "300 seconds (5 minutes) maximum". */
export const THREADS_VIDEO_MAX_SECONDS = 300;
/** Image ceiling: "8 MB maximum". */
export const THREADS_IMAGE_MAX_BYTES = 8 * 1024 * 1024;

/**
 * Threads image formats: "JPEG and PNG image types are the officially
 * supported formats for image posts."
 *
 * 🔴 WebP is NOT supported, and this matters concretely in Mora:
 * `src/services/media/imageCompression.ts` emits `image/webp` by DEFAULT
 * (`format === 'auto' || format === 'webp'`), and the media library is already
 * full of `.webp` assets. Publishing a Mora-generated image straight to Threads
 * would fail at the API. Fails CLOSED on unknown/extensionless URLs, matching
 * `isTikTokSupportedPhotoUrl`'s posture.
 */
const THREADS_IMAGE_RE = /\.(jpe?g|png)(?:[?#].*)?$/i;

export function isThreadsSupportedImageUrl(url: string): boolean {
  return THREADS_IMAGE_RE.test(url);
}

export type ThreadsMediaType = 'TEXT' | 'IMAGE' | 'VIDEO' | 'CAROUSEL';

export interface ThreadsContainerBody {
  media_type: ThreadsMediaType;
  /** Post text. Required for TEXT; optional caption for media/carousel parents. */
  text?: string;
  /** Public URL, IMAGE containers only. */
  image_url?: string;
  /** Public URL, VIDEO containers only. */
  video_url?: string;
  /** True for each child of a carousel; children are never published directly. */
  is_carousel_item?: boolean;
  /** Child container ids, CAROUSEL parents only. */
  children?: string[];
}

/** Trim to a hard character ceiling without splitting on a surrogate pair boundary. */
function clampText(value: string, max: number): string {
  if (value.length <= max) return value;
  const sliced = value.slice(0, max);
  // Don't leave a dangling high surrogate — that renders as U+FFFD.
  const lastCode = sliced.charCodeAt(sliced.length - 1);
  return lastCode >= 0xd800 && lastCode <= 0xdbff ? sliced.slice(0, -1) : sliced;
}

/** A text-only Threads post. Mora's ONLY text-without-media publish path. */
export function threadsTextContainerBody(text: string): ThreadsContainerBody {
  return { media_type: 'TEXT', text: clampText(text, THREADS_TEXT_MAX_CHARS) };
}

/**
 * A single image or video container. `isCarouselItem` marks it as a child, in
 * which case Threads ignores caption text on the child (the parent carries it),
 * so we omit `text` entirely rather than sending a value that gets dropped.
 */
export function threadsMediaContainerBody(args: {
  url: string;
  kind: 'IMAGE' | 'VIDEO';
  text?: string;
  isCarouselItem?: boolean;
}): ThreadsContainerBody {
  const body: ThreadsContainerBody = { media_type: args.kind };
  if (args.kind === 'IMAGE') body.image_url = args.url;
  else body.video_url = args.url;
  if (args.isCarouselItem) {
    body.is_carousel_item = true;
  } else if (args.text) {
    body.text = clampText(args.text, THREADS_TEXT_MAX_CHARS);
  }
  return body;
}

/** The CAROUSEL parent that stitches already-created child containers together. */
export function threadsCarouselContainerBody(args: {
  children: string[];
  text?: string;
}): ThreadsContainerBody {
  const body: ThreadsContainerBody = { media_type: 'CAROUSEL', children: args.children };
  if (args.text) body.text = clampText(args.text, THREADS_TEXT_MAX_CHARS);
  return body;
}

/**
 * Validation error message, or `null` if this media set fits Threads' publish
 * constraints. Pure; the caller (`post-to-threads/index.ts`) decides how to
 * surface it (thrown as a `PostingError`), and `shared/posting-readiness.ts`'s
 * `threads_*` issues mirror these same ceilings pre-flight — this
 * publisher-side copy is the defense-in-depth backstop for callers that bypass
 * readiness.
 */
export function validateThreadsMedia(args: {
  mediaUrls: string[];
  text: string;
  isVideoUrl: (url: string) => boolean;
}): string | null {
  const { mediaUrls, text } = args;

  // Text-only: valid on Threads (unlike Instagram/TikTok), but needs actual text.
  if (mediaUrls.length === 0) {
    return text.trim().length === 0 ? 'Threads posts need either text or media' : null;
  }

  if (mediaUrls.length > THREADS_CAROUSEL_MAX_CHILDREN) {
    return `Threads carousels support up to ${THREADS_CAROUSEL_MAX_CHILDREN} items`;
  }

  // Images must be JPEG/PNG. Videos are checked by extension elsewhere; only
  // non-video URLs are held to the image-format rule.
  const badImage = mediaUrls.find(
    (url) => !args.isVideoUrl(url) && !isThreadsSupportedImageUrl(url),
  );
  if (badImage) {
    return 'Threads accepts JPEG or PNG images only';
  }

  return null;
}
